import type { ReactNode } from 'react';
import styles from './Loader.module.css';

export type LoaderProps = {
  label?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Default `trace` — rounded-square path draw. `arc` = legacy conic. `mark` = brand monogram. */
  variant?: 'trace' | 'arc' | 'mark';
  tone?: 'default' | 'onBrand' | 'onDanger';
};

function TraceGlyph({ sizeClass, toneClass }: { sizeClass: string; toneClass?: string }) {
  return (
    <svg
      className={[styles.trace, sizeClass, toneClass].filter(Boolean).join(' ')}
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      aria-hidden
      focusable="false"
    >
      {/* Track — full rounded square */}
      <rect
        className={styles.traceTrack}
        x="2.5"
        y="2.5"
        width="19"
        height="19"
        rx="4.5"
        ry="4.5"
        pathLength={100}
        fill="none"
      />
      {/* Trace stroke — segment that travels the path */}
      <rect
        className={styles.traceStroke}
        x="2.5"
        y="2.5"
        width="19"
        height="19"
        rx="4.5"
        ry="4.5"
        pathLength={100}
        fill="none"
      />
      {/* Menthol tip — shorter head on the leading edge */}
      <rect
        className={styles.traceTip}
        x="2.5"
        y="2.5"
        width="19"
        height="19"
        rx="4.5"
        ry="4.5"
        pathLength={100}
        fill="none"
      />
    </svg>
  );
}

export function Loader({ label, size = 'md', variant = 'trace', tone = 'default' }: LoaderProps) {
  const sizeClass = styles[`size_${size}`];
  const toneClass =
    tone === 'onBrand' ? styles.tone_onBrand : tone === 'onDanger' ? styles.tone_onDanger : undefined;
  const wrapClass = variant === 'mark' && label ? styles.wrap_mark : undefined;

  if (variant === 'mark') {
    return (
      <span className={`${styles.wrap} ${wrapClass ?? ''}`.trim()} role="status" aria-live="polite">
        <span className={`${styles.mark} ${sizeClass}`} aria-hidden>
          <span className={[styles.monogram, toneClass].filter(Boolean).join(' ')} />
          <span className={[styles.arc, sizeClass, toneClass].filter(Boolean).join(' ')} />
        </span>
        {label ? <span>{label}</span> : null}
      </span>
    );
  }

  if (variant === 'arc') {
    return (
      <span className={styles.wrap} role="status" aria-live="polite">
        <span className={[styles.arc, sizeClass, toneClass].filter(Boolean).join(' ')} aria-hidden />
        {label ? <span>{label}</span> : null}
      </span>
    );
  }

  return (
    <span className={styles.wrap} role="status" aria-live="polite">
      <TraceGlyph sizeClass={sizeClass} toneClass={toneClass} />
      {label ? <span>{label}</span> : null}
    </span>
  );
}
